import React, { useMemo, useState ,useEffect} from "react";
import { DataGrid } from "@mui/x-data-grid";
import Lottie from "lottie-react";
import loader from "../assets/loader.json";
import Navbar from "../Components/Navbar";
import { useListEnquiryQuery, useApproveEnquiryMutation, useRejectEnquiryMutation } from "../redux/features/user/userApi";
import { ChevronDown, ChevronRight, X } from "lucide-react";

const formatDateTime = (iso) => {
  try {
    if (!iso) return "N/A";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_) {
    return String(iso ?? "N/A");
  }
};

const Enquiry = () => {
  const { data, isLoading, error, refetch } = useListEnquiryQuery();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // "all", "approved", "rejected", "pending"
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [approveModal, setApproveModal] = useState({ isOpen: false, enquiryId: null });
  const [rejectModal, setRejectModal] = useState({ isOpen: false, enquiryId: null, reason: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [approveEnquiry, { isLoading: isApproving }] = useApproveEnquiryMutation();
  const [rejectEnquiry, { isLoading: isRejecting }] = useRejectEnquiryMutation();

  const handleApproveEnquiry = async (enquiryId) => {
    try {
      const result = await approveEnquiry(enquiryId).unwrap();
      setApproveModal({ isOpen: false, enquiryId: null });
      alert('Enquiry approved successfully!');
      refetch(); // Refresh the enquiry data
    } catch (error) {
      console.error('Error approving enquiry:', error);
      const errorMessage = error?.data?.message || error?.message || 'Failed to approve enquiry';
      alert(errorMessage);
      refetch(); // Refresh the enquiry data

    }
  };

  const handleRejectEnquiry = async (enquiryId, reason) => {
    try {
      const result = await rejectEnquiry({ enquiryId, reason }).unwrap();
      setRejectModal({ isOpen: false, enquiryId: null, reason: "" });
      alert('Enquiry rejected successfully!');
      refetch(); // Refresh the enquiry data
    } catch (error) {
      console.error('Error rejecting enquiry:', error);
      const errorMessage = error?.data?.message || error?.message || 'Failed to reject enquiry';
      refetch(); // Refresh the enquiry data
      alert(errorMessage);
      setRejectModal({ isOpen: false, enquiryId: rejectModal.enquiryId, reason: "" });
    }
  };


  const rows = useMemo(() => {
    const raw =
      data?.data ||
      data?.enquiries ||
      data?.enquiry ||
      data?.result ||
      data?.results ||
      [];

    const list = Array.isArray(raw)
      ? raw
      : raw?.data && Array.isArray(raw.data)
        ? raw.data
        : [];

    return (list || []).map((item, index) => {
      const name =
        item?.name ||
        item?.username ||
        item?.fullName ||
        [item?.firstName, item?.lastName].filter(Boolean).join(" ") ||
        "N/A";

      return {
        id: index + 1,
        PlanKey: item?.PlanKey || "N/A",
        name,
        email: item?.email || item?.userEmail || "N/A",
        phone: item?.phone || item?.mobile || item?.contact || "N/A",
        planname: item?.planname || "N/A",
        planquote: item?.planquote || "N/A",
        roi: item?.roi || "N/A",
        term: item?.term || "N/A",
        udate: item?.udate || "N/A",
        allocations: item?.allocations || {},
        status: item?.status || "N/A",
      };
    });
  }, [data]);

  const filteredRows = useMemo(() => {
    let filtered = rows;

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((r) => r.status === parseInt(statusFilter));
    }

    // Apply search filter
    const term = search.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter((r) => {
        const hay = [r.PlanKey, r.name, r.email, r.phone, r.planname, r.planquote, r.roi, r.term, r.udate, r.status, JSON.stringify(r.allocations)]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(term);
      });
    }

    return filtered;
  }, [rows, search, statusFilter]);

  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredRows.slice(startIndex, endIndex);
  }, [filteredRows, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredRows.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  const handleExportCSV = () => {
    if (!filteredRows.length) return;

    const headers = ["S.No", "Name", "Email", "Phone", "planname", "planquote", "roi", "term", "date", "allocations"];

    const csvRows = [
      headers.join(","),

      ...filteredRows.map((row) =>
        [
          `"${row.PlanKey}"`,
          `"${row.name}"`,
          `"${row.email}"`,
          `"${row.phone}"`,
          `"${row.planname}"`,
          `"${row.planquote}"`,
          `"${row.roi}"`,
          `"${row.term}"`,
          `"${row.udate}"`,
          `"${row.status}"`,
          `"${JSON.stringify(row.allocations)}"`
        ].join(",")
      ),
    ];

    const blob = new Blob([csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "enquiries.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const style = { height: 120, width: 120 };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Lottie animationData={loader} style={style} loop />
      </div>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 pt-20 px-4">
          <div className="max-w-6xl mx-auto bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-600 font-semibold">
              Error loading enquiries. Please try again later.
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-20 md:pt-24 pb-8 px-3 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="w-full border rounded-lg bg-white shadow-lg overflow-hidden">

            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 sm:p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-900">
                Enquiries
              </h1>

              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <span className="text-sm font-semibold text-gray-600">
                  Total:{" "}
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-bold">
                    {filteredRows.length}
                  </span>
                </span>

                <button
                  onClick={handleExportCSV}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow transition w-full sm:w-auto"
                >
                  Export CSV
                </button>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="p-4 sm:p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <div 
                  onClick={() => setStatusFilter("all")}
                  className={`bg-white rounded-lg p-3 sm:p-4 border shadow-sm cursor-pointer transition-all duration-200 ${
                    statusFilter === "all" 
                      ? "border-blue-400 bg-blue-50 shadow-md transform scale-105" 
                      : "border-blue-200 hover:border-blue-300 hover:shadow-md hover:transform hover:scale-102"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-medium">Total</p>
                      <p className="text-xl sm:text-2xl font-bold text-blue-900">{rows.length}</p>
                    </div>
                    <div className="bg-blue-100 rounded-full p-2 sm:p-3">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div 
                  onClick={() => setStatusFilter("2")}
                  className={`bg-white rounded-lg p-3 sm:p-4 border shadow-sm cursor-pointer transition-all duration-200 ${
                    statusFilter === "2" 
                      ? "border-yellow-400 bg-yellow-50 shadow-md transform scale-105" 
                      : "border-yellow-200 hover:border-yellow-300 hover:shadow-md hover:transform hover:scale-102"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-medium">Pending</p>
                      <p className="text-xl sm:text-2xl font-bold text-yellow-600">{rows.filter(r => r.status === 2).length}</p>
                    </div>
                    <div className="bg-yellow-100 rounded-full p-2 sm:p-3">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div 
                  onClick={() => setStatusFilter("1")}
                  className={`bg-white rounded-lg p-3 sm:p-4 border shadow-sm cursor-pointer transition-all duration-200 ${
                    statusFilter === "1" 
                      ? "border-green-400 bg-green-50 shadow-md transform scale-105" 
                      : "border-green-200 hover:border-green-300 hover:shadow-md hover:transform hover:scale-102"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-medium">Approved</p>
                      <p className="text-xl sm:text-2xl font-bold text-green-600">{rows.filter(r => r.status === 1).length}</p>
                    </div>
                    <div className="bg-green-100 rounded-full p-2 sm:p-3">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div 
                  onClick={() => setStatusFilter("0")}
                  className={`bg-white rounded-lg p-3 sm:p-4 border shadow-sm cursor-pointer transition-all duration-200 ${
                    statusFilter === "0" 
                      ? "border-red-400 bg-red-50 shadow-md transform scale-105" 
                      : "border-red-200 hover:border-red-300 hover:shadow-md hover:transform hover:scale-102"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-medium">Rejected</p>
                      <p className="text-xl sm:text-2xl font-bold text-red-600">{rows.filter(r => r.status === 0).length}</p>
                    </div>
                    <div className="bg-red-100 rounded-full p-2 sm:p-3">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="p-4 sm:p-6 border-b bg-gray-50">
              <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
                <div className="flex-1 w-full">
                  <input
                    type="text"
                    placeholder="Search name, email, phone, planname, planquote, roi, term, allocations..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="border border-gray-300 px-4 py-2.5 rounded-lg w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setStatusFilter("all")}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                      statusFilter === "all"
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setStatusFilter("2")}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                      statusFilter === "2"
                        ? "bg-yellow-600 text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    Pending
                  </button>
                  <button
                    onClick={() => setStatusFilter("1")}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                      statusFilter === "1"
                        ? "bg-green-600 text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    Approved
                  </button>
                  <button
                    onClick={() => setStatusFilter("0")}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                      statusFilter === "0"
                        ? "bg-red-600 text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    Rejected
                  </button>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="p-2 sm:p-4 lg:p-6 overflow-x-auto">
              {filteredRows.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <div className="max-w-md mx-auto">
                    <div className="bg-gray-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-gray-500 text-sm sm:text-base">
                      {search
                        ? "No enquiries found matching your search."
                        : "No enquiries available."}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Table Headers - Hidden on mobile, shown on larger screens */}
                  <div className="hidden sm:block bg-gray-100 rounded-lg p-3 sm:p-4 border">
                    <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-9 gap-2 sm:gap-4 items-center">
                      <div className="text-xs sm:text-sm font-semibold text-gray-700">ID</div>
                      {/* <div className="text-xs sm:text-sm font-semibold text-gray-700 col-span-2 md:col-span-2 lg:col-span-2">Name</div> */}
                      <div className="text-xs sm:text-sm font-semibold text-gray-700 hidden md:block">Email</div>
                      <div className="text-xs sm:text-sm font-semibold text-gray-700 hidden lg:block">Phone</div>
                      <div className="text-xs sm:text-sm font-semibold text-gray-700">Plan</div>
                      <div className="text-xs sm:text-sm font-semibold text-gray-700 hidden lg:block">Quote</div>
                      <div className="text-xs sm:text-sm font-semibold text-gray-700 hidden lg:block">ROI</div>
                      <div className="text-xs sm:text-sm font-semibold text-gray-700 hidden lg:block">Term</div>
                      <div className="text-xs sm:text-sm font-semibold text-gray-700 hidden lg:block">Date</div>
                      <div className="text-xs sm:text-sm font-semibold text-gray-700 hidden lg:block">Status</div>
                    </div>
                  </div>

                  {paginatedRows.map((row) => (
                    <div key={row.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                      {/* Mobile Card Layout */}
                      <div className="sm:hidden">
                        <div
                          className="p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => {
                            const newExpanded = new Set(expandedRows);
                            if (newExpanded.has(row.id)) {
                              newExpanded.delete(row.id);
                            } else {
                              newExpanded.add(row.id);
                            }
                            setExpandedRows(newExpanded);
                          }}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-blue-600">#{row.PlanKey}</span>
                              <span className="text-sm font-medium text-gray-900">{row.name}</span>
                            </div>
                            {expandedRows.has(row.id) ? (
                              <ChevronDown className="w-4 h-4 text-gray-600" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-600" />
                            )}
                          </div>
                          <div className="space-y-1.5 text-xs text-gray-600">
                            <div className="flex justify-between">
                              <span className="font-medium">Email:</span>
                              <span className="truncate ml-2 flex-1 text-right">{row.email}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium">Phone:</span>
                              <span className="truncate ml-2 flex-1 text-right">{row.phone}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium">Plan:</span>
                              <span className="truncate ml-2 flex-1 text-right">{row.planname}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium">Quote:</span>
                              <span className="truncate ml-2 flex-1 text-right">{row.planquote}</span>
                            </div>
                            <div className="grid grid-cols-3 gap-2 mt-2">
                              <div className="text-center">
                                <span className="font-medium block">ROI:</span>
                                <span className="truncate">{row.roi}</span>
                              </div>
                              <div className="text-center">
                                <span className="font-medium block">Term:</span>
                                <span className="truncate">{row.term}</span>
                              </div>
                              <div className="text-center">
                                <span className="font-medium block">Date:</span>
                                <span className="truncate">{row.udate}</span>
                              </div>
                              <div className="text-center">
                                <span className="font-medium block">Status:</span>
                                {row.status === 0 ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    Rejected
                                  </span>
                                ) : row.status === 1 ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Approved
                                  </span>
                                ) : row.status === 2 ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    Pending
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    {row.status}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="mt-2 flex items-center justify-between">
                            <span className="text-xs text-gray-500">
                              {row.allocations && typeof row.allocations === 'object'
                                ? `${Object.keys(row.allocations).length} allocations`
                                : 'No allocations'
                              }
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Desktop Table Layout */}
                      <div className="hidden sm:block">
                        <div
                          className="p-3 sm:p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => {
                            const newExpanded = new Set(expandedRows);
                            if (newExpanded.has(row.id)) {
                              newExpanded.delete(row.id);
                            } else {
                              newExpanded.add(row.id);
                            }
                            setExpandedRows(newExpanded);
                          }}
                        >
                          <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-9 gap-2 sm:gap-4 items-center">
                            <div className="text-xs sm:text-sm font-medium text-gray-900 truncate">{row.PlanKey}</div>
                            {/* <div className="text-xs sm:text-sm text-gray-900 truncate col-span-2 md:col-span-2 lg:col-span-2" title={row.name}>{row.name}</div> */}
                            <div className="text-xs sm:text-sm text-gray-600 truncate hidden md:block" title={row.email}>{row.email}</div>
                            <div className="text-xs sm:text-sm text-gray-600 truncate hidden lg:block" title={row.phone}>{row.phone}</div>
                            <div className="text-xs sm:text-sm text-gray-900 truncate" title={row.planname}>{row.planname}</div>
                            <div className="text-xs sm:text-sm text-gray-900 truncate hidden lg:block" title={row.planquote}>{row.planquote}</div>
                            <div className="text-xs sm:text-sm text-gray-900 truncate hidden lg:block">{row.roi}</div>
                            <div className="text-xs sm:text-sm text-gray-900 truncate hidden lg:block">{row.term}</div>
                            <div className="text-xs sm:text-sm text-gray-900 truncate hidden lg:block">{row.udate}</div>
                            <div className="hidden lg:block">
                              {row.status === 0 ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  Rejected
                                </span>
                              ) : row.status === 1 ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Approved
                                </span>
                              ) : row.status === 2 ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Pending
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  {row.status}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="mt-2 sm:mt-3 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {expandedRows.has(row.id) ? (
                                <ChevronDown className="w-4 h-4 text-gray-600" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-600" />
                              )}
                              <span className="text-xs sm:text-sm text-gray-600">
                                {row.allocations && typeof row.allocations === 'object'
                                  ? `${Object.keys(row.allocations).length} allocations`
                                  : 'No allocations'
                                }
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {expandedRows.has(row.id) && row.allocations && typeof row.allocations === 'object' && (
                        <div className="border-t bg-gray-50 p-3 sm:p-4">
                          <div className="mb-3 sm:mb-4">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2 sm:mb-3">Allocation Details - {row.PlanKey}</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                              {Object.entries(row.allocations).map(([coin, data]) => (
                                <div key={coin} className="bg-white rounded-lg p-2 sm:p-3 border border-blue-200 shadow-sm hover:shadow-md transition-shadow">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="font-bold text-blue-900 uppercase text-xs sm:text-sm">{coin}</span>
                                    <span className="text-green-700 font-semibold text-xs sm:text-sm">${data.usd}</span>
                                  </div>
                                  <div className="space-y-1 text-xs">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Amount:</span>
                                      <span className="font-mono text-gray-900 truncate max-w-[60%]">
                                        {typeof data.amount === 'number' ? data.amount.toFixed(8) : data.amount}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Market Price:</span>
                                      <span className="font-mono text-gray-900">${data.price}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {row.status === 2 ?

                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                              <button
                                onClick={() => setApproveModal({ isOpen: true, enquiryId: row.PlanKey })}
                                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold shadow transition w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={isApproving || isRejecting}
                              >
                                {isApproving ? 'Approving...' : 'Approve'}
                              </button>
                              <button
                                onClick={() => setRejectModal({ isOpen: true, enquiryId: row.PlanKey })}
                                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold shadow transition w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={isApproving || isRejecting}
                              >
                                {isRejecting ? 'Rejecting...' : 'Reject'}
                              </button>
                            </div>
                            : null}
                        </div>
                      )}
                    </div>
                  ))}
                  {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50 mt-4 rounded-b-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredRows.length)} of {filteredRows.length} entries
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="text-sm text-gray-600">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
                </div>

                
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Approve Confirmation Modal */}
      {approveModal.isOpen && (

        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-4 sm:p-6 max-w-md w-full max-h-[90vh] overflow-y-auto mx-auto shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Confirm Approval {approveModal.enquiryId}</h3>
              <button
                onClick={() => setApproveModal({ isOpen: false, enquiryId: approveModal.enquiryId })}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-blue-800 text-sm">
                  <span className="font-semibold">Enquiry #{approveModal.enquiryId}</span>
                </p>
              </div>
              <p className="text-gray-600 text-sm sm:text-base">
                Are you sure you want to approve this enquiry? This action cannot be undone.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <button
                onClick={() => setApproveModal({ isOpen: false, enquiryId: approveModal.enquiryId })}
                className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition font-medium text-sm w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isApproving}
              >
                Cancel
              </button>
              <button
                onClick={() => handleApproveEnquiry(approveModal.enquiryId)}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg font-semibold transition text-sm w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isApproving}
              >
                {isApproving ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Approving...
                  </span>
                ) : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal with Reason Input */}
      {rejectModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-4 sm:p-6 max-w-md w-full max-h-[90vh] overflow-y-auto mx-auto shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Reject Enquiry</h3>
              <button
                onClick={() => setRejectModal({ isOpen: false, enquiryId: rejectModal.enquiryId, reason: "" })}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-red-800 text-sm">
                  <span className="font-semibold">Enquiry #{rejectModal.enquiryId}</span>
                </p>
              </div>
              <p className="text-gray-600 text-sm sm:text-base mb-4">
                Please provide a reason for rejecting this enquiry:
              </p>
              <textarea
                value={rejectModal.reason}
                onChange={(e) => setRejectModal({ ...rejectModal, reason: e.target.value })}
                placeholder="Enter rejection reason..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 resize-none focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm sm:text-base"
                rows={4}
              />
              {!rejectModal?.reason?.trim() && (
                <p className="text-red-500 text-xs mt-1">Reason is required</p>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <button
                onClick={() => setRejectModal({ isOpen: false, enquiryId: rejectModal.enquiryId, reason: "" })}
                className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition font-medium text-sm w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isRejecting}
              >
                Cancel
              </button>
              <button
                onClick={() => handleRejectEnquiry(rejectModal.enquiryId, rejectModal.reason)}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-lg font-semibold transition text-sm w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isRejecting || !rejectModal?.reason?.trim()}
              >
                {isRejecting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Rejecting...
                  </span>
                ) : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Enquiry;
